import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { log, setLogUser } from '../lib/logger'

const AuthContext = createContext({})

function loadSelectedCompany() {
  try { return JSON.parse(localStorage.getItem('selectedCompany')) } catch { return null }
}

// Filtro de gestor compartilhado entre as telas (Home, Indicadores, Dashboard,
// Fcas e as tres Analytics). '' = todos. Fica em sessionStorage de proposito:
// acompanha a navegacao e sobrevive ao F5, mas nao volta em uma nova sessao —
// senao o usuario abriria a plataforma dias depois com a tela filtrada sem
// lembrar por que, achando que sumiram indicadores.
function loadFiltroGestor() {
  try { return sessionStorage.getItem('filtroGestor') || '' } catch { return '' }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedCompany, setSelectedCompanyState] = useState(loadSelectedCompany)
  const [filtroGestor, setFiltroGestorState] = useState(loadFiltroGestor)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        setLogUser(session.user.id)
        fetchProfile(session.user.id)
      } else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        setLogUser(session.user.id)
        fetchProfile(session.user.id)
        if (event === 'SIGNED_IN') log('login', 'auth', session.user.id, { email: session.user.email })
      } else if (event === 'SIGNED_OUT') {
        setLogUser(null)
        setProfile(null)
        setLoading(false)
        setFiltroGestor('')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    setProfile(data)
    setLoading(false)
  }

  async function refreshProfile() {
    const { data: { user: currentUser } } = await supabase.auth.getUser()
    if (currentUser) await fetchProfile(currentUser.id)
  }

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  function setSelectedCompany(company) {
    setSelectedCompanyState(company)
    if (company) localStorage.setItem('selectedCompany', JSON.stringify(company))
    else localStorage.removeItem('selectedCompany')
    // O gestor filtrado e' de uma empresa so'; ao trocar de tenant o id
    // deixa de existir na lista e o filtro perde o sentido.
    setFiltroGestor('')
  }

  function setFiltroGestor(gestorId) {
    const valor = gestorId || ''
    setFiltroGestorState(valor)
    try {
      if (valor) sessionStorage.setItem('filtroGestor', valor)
      else sessionStorage.removeItem('filtroGestor')
    } catch { /* modo privado/storage bloqueado: mantem so' em memoria */ }
  }

  const isSuperAdmin = profile?.role === 'super_admin'
  const isAdmin = profile?.role === 'admin'
  const isGestor = profile?.role === 'gestor'
  const isCoordenador = profile?.role === 'coordenador'

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      isSuperAdmin, isAdmin, isGestor, isCoordenador,
      selectedCompany, setSelectedCompany,
      filtroGestor, setFiltroGestor,
      signIn, signOut, refreshProfile
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
